<template>
  <div class="navigation">
    <span
      id="navigation-title"
      class="visually-hidden"
      v-text="$string('general.site_header.navigation')"
    />

    <nav class="navigation__nav">
      <ul class="navigation__list">
        <template
          v-for="tier1 of desktop.items"
          :key="tier1.handle"
        >
          <li class="navigation__item">
            <button
              v-if="tier1.children"
              type="button"
              class="navigation__link text-body-m-bold"
              @click="handleMegamenu(tier1.title)"
              v-text="tier1.title"
            />

            <a
              v-else
              class="navigation__link text-body-m-bold"
              :href="tier1.url"
              v-text="tier1.title"
            />
          </li>
        </template>
      </ul>
    </nav>

    <teleport to="body">
      <overlay
        key="megamenuOverlay"
        direction="right"
        namespace="megamenuOverlay"
        :template="false"
        type="drawer"
        block-class="megamenu"
      >
        <template #body>
          <div class="megamenu">
            <template v-if="megamenu">
              <button
                type="button"
                class="megamenu__close"
                @click="closeOverlay('megamenuOverlay')"
              >
                <span
                  class="visually-hidden"
                  v-text="$string('accessibility.close')"
                />

                <icon-close />
              </button>

              <nav class="megamenu__list">
                <ul class="megamenu__parent">
                  <li
                    v-for="(item, index) of desktop.items"
                    :key="index"
                    class="megamenu__parent-item"
                  >
                    <button
                      v-if="item.children"
                      class="text-body-m-bold"
                      :class="{ [$class('active')]: megamenu.title === item.title }"
                      type="button"
                      @mouseenter="handleMegamenu(item.title)"
                      v-text="item.title"
                    />

                    <a
                      v-else
                      class="text-body-m-bold"
                      :class="{ [$class('active')]: megamenu.title === item.title }"
                      :href="item.url"
                      v-text="item.title"
                    />
                  </li>
                </ul>

                <ul class="megamenu__link-wrapper">
                  <li
                    v-for="link of megamenu.children"
                    :key="link.url"
                    class="megamenu__item"
                  >
                    <a
                      class="megamenu__link text-heading-xl"
                      :href="link.url"
                      @mouseenter="hoverMedia.src = link.title.split(' | ')[1] || null"
                      @mouseleave="hoverMedia.src = null"
                      v-text="link.title.split(' | ')[0]"
                    />
                  </li>
                </ul>
              </nav>

              <media-block
                v-if="megamenu.media"
                :media="megamenu.media"
                :ratio="0.58"
                block-class="megamenu"
                :custom-image="hoverMedia"
              />
            </template>
          </div>
        </template>
      </overlay>
    </teleport>
  </div>
</template>

<script>

/**
 * Vue: Navigation (site-header)
 * -----------------------------------------------------------------------------
 * Desktop navigation links.
 * - No style import as it's included in critical stylesheet.
 *
 * @param {Object} desktop - Desktop linklist handle and items.
 *
 */

import { mapActions } from 'vuex'
import MediaBlock from '~async/menu-drawer/media-block'
import Overlay from '~global/overlay/overlay'
import IconClose from '~icons/directional-navigation/close.svg'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Navigation',

  components: {
    Overlay,
    IconClose,
    MediaBlock,
  },

  props: {
    desktop: {
      type: Object,
      default: () => ({
        handle: '',
        items: () => [],
      }),
    },
  },

  data: () => {
    return {
      megamenu: null,
      hoverMedia: {
        src: null,
        width: 525,
        height: 900,
      },
    }
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
    }),

    /**
     * Open corresponding megamenu based on title.
     * @param {String} title - Link title
     */
    handleMegamenu(title) {
      const megamenu = this.desktop.items.find(item => item.title === title)

      if (!megamenu || !megamenu.children) {
        this.closeMegamenu()
        return
      }

      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'megamenuOverlay',
      })

      this.megamenu = megamenu
    },
  },
}
</script>
